import { Icon, Spinner } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { batch, shallowEqual } from "react-redux"

import { useAppDispatch, useAppSelector } from "@/App"
import { setDate } from "@/store/global"
import { selectOrderPoint } from "@/store/orders"
import { setAboutPanel, setShowDriverDetails, setShowOrderDetails } from "@/store/ui"
import { Order } from "@/types"
import { formatDelay, formatTime, getIconFor, OrderUtils } from "@/utils"

import cls from "./OrderPoint.module.scss"

type OrderPointProps = {
  orderId: Order["id"]
  type: "pickup" | "delivery"
  showDetails: () => void
}

export const OrderPoint = memo(({ orderId, type, showDetails }: OrderPointProps) => {
  const point = useAppSelector(selectOrderPoint(orderId, type), shallowEqual)

  if (!point) return <EmptyPoint type={type} />

  const delay = point.late

  const pointTime = OrderUtils.getPointTime(point)

  return (
    <div className={cls.point} onClick={showDetails} role="button">
      <div className={cls.statusIcon}>
        <Icon name={getIconFor(point.state)} className={clsx(cls.icon, `status-${point.state}`)} />
      </div>

      <div className={clsx(cls.time, `status-${point.state}`)}>
        {OrderUtils.shouldShowTime(point) ? pointTime && formatTime(pointTime) : <Spinner size="small" inline />}
      </div>

      <div className={cls.content}>
        <div className={cls.header}>
          <div className={cls.name}>{point.name ? point.name : point.phone}</div>
          <div className={clsx(cls.delay, `text-${OrderUtils.getPointDelayColor(delay)}`)}>
            {OrderUtils.shouldShowDelay(point, delay) && formatDelay(delay!)}
          </div>
        </div>

        <div className={cls.address}>{point.address}</div>
      </div>
    </div>
  )
})

const EmptyPoint = memo(({ type }: { type: "pickup" | "delivery" }) => {
  const { t } = useTranslation()

  return (
    <div className={clsx(cls.point, cls.emptyPoint)}>
      <div className={cls.statusIcon}>
        <Icon name={type} className={cls.icon} />
      </div>

      <div className={cls.time}>-</div>

      <div className={cls.content}>{type === "delivery" ? t("pickup_only") : t("delivery_only")}</div>
    </div>
  )
})

type ReturnedOrderPointProps = {
  id: number
  uid: string
  pickup_at: string | null
  driver_id: number | null
}

export const ReturnedOrderPoint = memo(({ id, uid, pickup_at, driver_id }: ReturnedOrderPointProps) => {
  const currentSelectedDate = useAppSelector((state) => state.global.date)

  const dispatch = useAppDispatch()

  function showDetails() {
    if (!pickup_at) return

    batch(() => {
      if (dayjs(pickup_at).isBefore(currentSelectedDate)) {
        dispatch(setDate(dayjs(pickup_at).format("YYYY-MM-DD")))
      }

      dispatch(setShowOrderDetails({ id, type: "pickup" }))
      dispatch(setAboutPanel({ type: "order", id }))

      if (driver_id) {
        dispatch(setShowDriverDetails(driver_id))
      }
    })
  }

  return (
    <div onClick={showDetails} className={clsx(cls.point, cls.emptyPoint)} role="button">
      <div className={cls.statusIcon}>
        <Icon name="pickup" className={cls.icon} />
      </div>

      <div className={cls.time}>-</div>

      <div className={cls.content}>
        Autozwrot zlecenia <span className="text-gray-80">#{uid}</span>
      </div>
    </div>
  )
})
